/*
 * TAZTIR 产品购买页交互。
 * 数据职责：Liquid / Shopify 负责价格、库存和规格组合；本文件只提交选择、切换图片。
 * 不保存整份 variants JSON，因此不会受到前端遍历 250 个规格的限制。
 * 所有事件绑定到当前分区，主题编辑器重新载入分区时自动清理；不修改 Dawn 全局组件。
 */
(() => {
  'use strict';
  if (customElements.get('taztir-purchase')) return;

  class TaztirPurchase extends HTMLElement {
    constructor() {
      super();
      this.current = 0;
      this.busy = '';
      this.requestVersion = 0;
      this.lockedControls = [];
      this.motion = new Set();
      this.mediaMotion = [];
    }

    connectedCallback() {
      if (this.events && !this.events.signal.aborted) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.gallery = this.querySelector('[data-gallery]');
      this.panels = [...(this.gallery?.querySelectorAll('[data-media-id]') || [])];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.committedOptions = this.optionValues();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');

      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('change', (event) => this.onChange(event), options);
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-quantity]')) this.syncQuantityButtons();
      }, options);
      this.addEventListener('submit', (event) => this.onSubmit(event), options);
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.gallery?.addEventListener('pointerdown', (event) => this.startSwipe(event), options);
      this.gallery?.addEventListener('pointerup', (event) => this.endSwipe(event), options);
      this.gallery?.addEventListener('pointercancel', () => { this.swipe = null; }, options);
      this.gallery?.addEventListener('pointermove', (event) => this.trackLight(event), options);
      this.gallery?.addEventListener('pointerleave', () => this.resetLight(), options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.stopMotion();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopMotion();
      }, options);
      this.dialog?.addEventListener('close', () => {
        if (this.zoomTrigger?.isConnected) this.zoomTrigger.focus({ preventScroll: true });
      }, options);

      const initial = this.findMedia(this.dataset.selectedMedia);
      this.classList.add('is-ready');
      this.selectMedia(initial >= 0 ? initial : Math.max(0, this.panels.findIndex((panel) => panel.classList.contains('is-active'))), false);
      if (this.panels.length) {
        this.gallery.setAttribute('role', 'button');
        this.gallery.tabIndex = 0;
        this.gallery.setAttribute('aria-label', 'Enlarge product image');
      }
      // 纵向缩略图的高度取实际方形主图，不让图片数量改变整个布局。
      if (window.ResizeObserver && this.gallery) {
        this.galleryResize = new ResizeObserver(([entry]) => {
          this.style.setProperty('--tpp-stage-height', `${Math.round(entry.contentRect.height)}px`);
        });
        this.galleryResize.observe(this.gallery);
      }
      this.setupEntrance();
      this.syncQuantityButtons();
      this.initializePayments();
      // 购物车抽屉删除/修改数量后，重新计算当前商品还能加入的数量。
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'taztir-purchase' || !this.isConnected) return;
          if (this.busy) this.pendingCartRefresh = true;
          else {
            return this.refreshAvailability(this.querySelector('[data-product-form] [name="id"]')?.value);
          }
        });
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.variantRequest?.abort();
      this.availabilityRequest?.abort();
      this.cartUnsubscribe?.();
      this.galleryResize?.disconnect();
      this.entranceObserver?.disconnect();
      this.stopMotion();
      this.requestVersion += 1;
      // 读取规格可以取消；加购是一次写入，绝不能因为分区卸载就取消后自动重试。
      if (this.busy === 'variant') {
        this.restoreOptions();
        this.clearBusy();
      }
      this.panels?.forEach((panel) => this.pauseMedia(panel));
      if (this.dialog?.open) this.dialog.close();
    }

    optionValues() {
      return [...this.querySelectorAll('[data-option]')].map((select) => select.value);
    }

    restoreOptions() {
      this.querySelectorAll('[data-option]').forEach((select, index) => {
        if (this.committedOptions[index] !== undefined) select.value = this.committedOptions[index];
      });
    }

    findMedia(id) {
      if (!id) return -1;
      return this.panels.findIndex((panel) => panel.id === String(id) || panel.dataset.mediaId === String(id));
    }

    onClick(event) {
      // 拖动切图后抑制同一次手势的 click；轻点仍可直接查看大图。
      if (event.target.closest('[data-gallery]')) {
        if (Date.now() < (this.suppressGalleryClickUntil || 0)) return;
        return this.openZoom(this.gallery);
      }
      const control = event.target.closest('button, [data-thumb]');
      if (!control || !this.contains(control) || control.disabled) return;
      if (control.matches('[data-thumb]')) this.selectMedia(this.findMedia(control.dataset.target));
      else if (control.matches('[data-prev]')) this.selectMedia(this.current - 1);
      else if (control.matches('[data-next]')) this.selectMedia(this.current + 1);
      else if (control.matches('[data-zoom]')) this.openZoom(control);
      else if (control.matches('[data-close-zoom]')) this.dialog?.close();
      else if (control.matches('[data-minus]')) this.adjustQuantity(-1);
      else if (control.matches('[data-plus]')) this.adjustQuantity(1);
    }

    onChange(event) {
      if (event.target.matches('[data-option]')) {
        if (this.busy === 'cart') return this.restoreOptions();
        this.updateVariant([...this.querySelectorAll('[data-option]')].indexOf(event.target));
      } else if (event.target.matches('[data-quantity]')) this.syncQuantityButtons();
    }

    onKeydown(event) {
      if (event.target === this.gallery && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.openZoom(this.gallery);
        return;
      }
      if (!event.target.closest('[data-thumb]') || this.panels.length < 2) return;
      let index;
      if (event.key === 'ArrowRight') index = this.current + 1;
      if (event.key === 'ArrowLeft') index = this.current - 1;
      if (event.key === 'ArrowDown') index = this.current + 1;
      if (event.key === 'ArrowUp') index = this.current - 1;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = this.panels.length - 1;
      if (index === undefined) return;
      event.preventDefault();
      this.selectMedia(index);
      this.thumbs.find((thumb) => thumb.getAttribute('aria-pressed') === 'true')?.focus({ preventScroll: true });
    }

    selectMedia(index, animate = true) {
      if (!this.panels.length || index < -1) return;
      const previous = this.panels[this.current];
      const direction = index < this.current ? -1 : 1;
      this.cancelMediaMotion();
      const total = this.panels.length;
      this.current = ((index % total) + total) % total;
      const selected = this.panels[this.current];
      const image = selected.querySelector('img');
      if (image) image.loading = 'eager';
      this.panels.forEach((panel, position) => {
        const active = position === this.current;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
        if (!active) this.pauseMedia(panel);
      });
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.target === selected.id || thumb.dataset.target === selected.dataset.mediaId;
        thumb.setAttribute('aria-pressed', String(active));
        thumb.classList.toggle('is-active', active);
        if (active) this.revealThumbnail(thumb);
      });
      const count = this.querySelector('[data-media-counter]');
      if (count) count.textContent = `${String(this.current + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      this.querySelectorAll('[data-prev], [data-next]').forEach((button) => { button.disabled = total < 2; });
      const zoom = this.querySelector('[data-zoom]');
      if (zoom) zoom.disabled = !selected.querySelector('img[data-zoom-src]');
      if (animate && previous !== selected) this.animateMedia(previous, selected, direction);
    }

    revealThumbnail(thumb) {
      // 桌面只移动纵向缩略条、手机只移动横向缩略条，不改变页面滚动位置。
      const rail = thumb.closest('.tpp-thumbs');
      if (!rail) return;
      const item = thumb.getBoundingClientRect();
      const bounds = rail.getBoundingClientRect();
      if (item.left < bounds.left) rail.scrollLeft += item.left - bounds.left - 4;
      else if (item.right > bounds.right) rail.scrollLeft += item.right - bounds.right + 4;
      if (rail.scrollHeight > rail.clientHeight + 1) {
        if (item.top < bounds.top) rail.scrollTop += item.top - bounds.top - 4;
        else if (item.bottom > bounds.bottom) rail.scrollTop += item.bottom - bounds.bottom + 4;
      }
    }

    /* V2 气流式交互：遮罩只负责照片切换，不模拟或夸大实物风速。
       原生 Web Animations 可取消，快速点击始终以最后一次选择为准。 */
    canAnimate() {
      return this.dataset.motion !== 'false' && !this.reducedMotion?.matches &&
        !document.hidden && typeof this.animate === 'function';
    }

    runAnimation(element, frames, options) {
      if (!element || !this.canAnimate()) return null;
      const animation = element.animate(frames, options);
      this.motion.add(animation);
      animation.finished.then(() => this.motion.delete(animation), () => this.motion.delete(animation));
      return animation;
    }

    cancelMediaMotion() {
      this.mediaMotion.forEach(animation => animation?.cancel());
      this.mediaMotion = [];
      this.panels?.forEach(panel => panel.classList.remove('is-leaving'));
    }

    animateMedia(previous, selected, direction) {
      if (!this.canAnimate()) return;
      const duration = Math.max(250, Math.min(700, Number(this.dataset.duration) || 450));
      previous?.classList.add('is-leaving');
      const reveal = this.runAnimation(selected, [
        { clipPath: direction > 0 ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)', transform: `translateX(${direction * 12}px)` },
        { clipPath: 'inset(0 0% 0 0%)', transform: 'translateX(0)' }
      ], { duration, easing: 'cubic-bezier(.16,1,.3,1)' });
      const fade = previous && this.runAnimation(previous, [{ opacity: 1 }, { opacity: .32 }], { duration, easing: 'ease-out' });
      this.mediaMotion.push(reveal, fade);
      reveal?.finished.then(() => previous?.classList.remove('is-leaving'), () => {});
      // Light traces stay at the photo frame's outer edge; no photo tint or surface distortion.
      this.querySelectorAll('.tpp-air-pass i').forEach((line, index) => {
        const trace = this.runAnimation(line, [
          { transform: `translateX(${direction > 0 ? 0 : 340}%)`, opacity: 0 },
          { opacity: .8, offset: .18 },
          { transform: `translateX(${direction > 0 ? 340 : 0}%)`, opacity: 0 }
        ], { duration: duration + 140, delay: index * 45, easing: 'cubic-bezier(.22,.5,.2,1)' });
        this.mediaMotion.push(trace);
      });
    }

    trackLight(event) {
      if (this.dataset.rgb === 'false' || this.reducedMotion.matches || !this.finePointer.matches || event.pointerType === 'touch') return;
      const bounds = this.gallery.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, (event.clientX - bounds.left) / bounds.width * 100));
      const y = Math.max(0, Math.min(100, (event.clientY - bounds.top) / bounds.height * 100));
      // At most one style write per frame; no perpetual requestAnimationFrame loop.
      this.lightPosition = { x, y };
      if (this.lightFrame) return;
      this.lightFrame = requestAnimationFrame(() => {
        this.lightFrame = 0;
        this.style.setProperty('--tpp-light-x', `${this.lightPosition.x.toFixed(1)}%`);
        this.style.setProperty('--tpp-light-y', `${this.lightPosition.y.toFixed(1)}%`);
        this.classList.add('is-light-active');
      });
    }

    resetLight() {
      cancelAnimationFrame(this.lightFrame);
      this.lightFrame = 0;
      this.classList.remove('is-light-active');
    }

    setupEntrance() {
      if (!this.canAnimate() || !window.IntersectionObserver) return;
      this.entranceObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          this.entranceObserver.unobserve(entry.target);
          if (entry.target.matches('.tpp-display')) {
            this.runAnimation(entry.target, [
              { clipPath: 'inset(0 12% 0 0)', opacity: .55 },
              { clipPath: 'inset(0 0% 0 0)', opacity: 1 }
            ], { duration: 650, easing: 'cubic-bezier(.16,1,.3,1)' });
          } else {
            this.runAnimation(entry.target, [{ transform: 'translateY(10px)', opacity: .45 }, { transform: 'translateY(0)', opacity: 1 }], { duration: 480, easing: 'cubic-bezier(.16,1,.3,1)' });
          }
        });
      }, { threshold: .15 });
      this.querySelectorAll('.tpp-heading, .tpp-display, .tpp-purchase').forEach(el => this.entranceObserver.observe(el));
    }

    stopMotion() {
      this.cancelMediaMotion();
      this.motion.forEach(animation => animation.cancel());
      this.motion.clear();
      this.resetLight();
    }

    pauseMedia(panel) {
      panel.querySelectorAll('video').forEach((video) => video.pause());
      panel.querySelectorAll('iframe').forEach((frame) => {
        try {
          const url = new URL(frame.src, location.href);
          const host = url.hostname;
          if (/(^|\.)youtube(?:-nocookie)?\.com$/.test(host)) {
            frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), url.origin);
          } else if (/(^|\.)vimeo\.com$/.test(host)) {
            frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), url.origin);
          }
        } catch (_) { /* 无效 iframe 地址不会妨碍正常图片切换。 */ }
      });
      panel.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
    }

    startSwipe(event) {
      if (!event.isPrimary || event.pointerType === 'mouse') return;
      if (event.target.closest('button, a, input, select, summary, video, iframe, model-viewer, dialog, [data-thumb]')) return;
      if (!this.panels[this.current]?.querySelector('img[data-zoom-src]')) return;
      this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, time: Date.now() };
    }

    endSwipe(event) {
      const start = this.swipe;
      this.swipe = null;
      if (!start || start.id !== event.pointerId) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Date.now() - start.time < 900 && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.suppressGalleryClickUntil = Date.now() + 500;
        this.selectMedia(this.current + (dx < 0 ? 1 : -1));
      }
    }

    openZoom(trigger) {
      const image = this.panels[this.current]?.querySelector('img[data-zoom-src]');
      if (!image?.dataset.zoomSrc) return;
      if (!this.dialog?.showModal) {
        window.open(image.dataset.zoomSrc, '_blank', 'noopener,noreferrer');
        return;
      }
      const expanded = this.dialog.querySelector('[data-zoom-image]');
      if (!expanded) return;
      expanded.src = image.dataset.zoomSrc;
      expanded.alt = image.alt;
      this.zoomTrigger = trigger;
      this.dialog.showModal();
      this.dialog.querySelector('[data-close-zoom]')?.focus();
    }

    adjustQuantity(direction) {
      if (this.busy) return;
      const input = this.querySelector('[data-quantity]');
      if (!input || input.disabled) return;
      try { direction > 0 ? input.stepUp() : input.stepDown(); } catch (_) { return; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncQuantityButtons() {
      if (this.busy) return;
      const input = this.querySelector('[data-quantity]');
      if (!input) return;
      const min = Number(input.min || 1);
      const max = input.max === '' ? Infinity : Number(input.max);
      const value = Number(input.value);
      const minus = this.querySelector('[data-minus]');
      const plus = this.querySelector('[data-plus]');
      if (minus) minus.disabled = input.disabled || value <= min;
      if (plus) plus.disabled = input.disabled || value >= max;
    }

    preserveQuantity(value) {
      const input = this.querySelector('[data-quantity]');
      if (!input || !Number.isFinite(value)) return;
      const min = Number(input.min || 1);
      const step = Number(input.step || 1);
      const max = input.max === '' ? Infinity : Number(input.max);
      const last = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity;
      input.value = Math.max(min, Math.min(last, min + Math.floor((value - min) / step) * step));
    }

    setBusy(kind) {
      if (!this.busy) {
        const selectors = '[data-submit], [data-quantity], [data-minus], [data-plus]' + (kind === 'cart' ? ', [data-option]' : '');
        this.lockedControls = [...this.querySelectorAll(selectors)].map((element) => ({ element, disabled: element.disabled }));
        this.lockedPayments = [...this.querySelectorAll('.shopify-payment-button')].map((element) => ({ element, inert: element.inert }));
      }
      this.busy = kind;
      this.lockedControls.forEach(({ element }) => { element.disabled = true; });
      this.lockedPayments?.forEach(({ element }) => { element.inert = true; });
      this.querySelector('[data-purchase]')?.setAttribute('aria-busy', 'true');
      this.querySelector('[data-submit]')?.setAttribute('aria-busy', 'true');
      this.classList.toggle('is-updating', kind === 'variant');
      this.classList.toggle('is-adding', kind === 'cart');
      if (kind === 'cart') {
        const label = this.querySelector('[data-submit-label]');
        if (label) {
          this.submitText = label.textContent;
          label.textContent = this.dataset.loadingLabel || 'Adding…';
        }
      }
    }

    clearBusy() {
      this.lockedControls.forEach(({ element, disabled }) => { if (this.contains(element)) element.disabled = disabled; });
      this.lockedPayments?.forEach(({ element, inert }) => { if (this.contains(element)) element.inert = inert; });
      this.lockedControls = [];
      this.lockedPayments = [];
      this.busy = '';
      this.classList.remove('is-updating', 'is-adding');
      this.querySelector('[data-purchase]')?.removeAttribute('aria-busy');
      this.querySelector('[data-submit]')?.removeAttribute('aria-busy');
      const label = this.querySelector('[data-submit-label]');
      if (label && this.submitText !== undefined) label.textContent = this.submitText;
      this.submitText = undefined;
      this.syncQuantityButtons();
      if (this.pendingCartRefresh && this.isConnected) {
        this.pendingCartRefresh = false;
        this.refreshAvailability(this.querySelector('[data-product-form] [name="id"]')?.value);
      }
    }

    initializePayments() {
      // 可选的 Shopify 快捷结账组件在服务端替换 HTML 后需要重新初始化。
      try { window.Shopify?.PaymentButton?.init(); } catch (_) { /* 常规加购不依赖快捷结账加载成功。 */ }
    }

    clearMessages() {
      this.querySelectorAll('[data-error], [data-success]').forEach((element) => { element.hidden = true; });
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    showSuccess(message) {
      const success = this.querySelector('[data-success]');
      if (!success) return;
      let label = success.querySelector('[data-success-message]');
      if (!label) {
        label = document.createElement('span');
        label.dataset.successMessage = '';
        success.prepend(label);
      }
      label.textContent = `${message} `;
      const link = success.querySelector('[data-cart-link]');
      if (link) { link.href = this.dataset.cartUrl; link.hidden = false; }
      success.hidden = false;
    }

    async updateVariant(focusIndex) {
      if (this.busy === 'cart') return;
      const version = ++this.requestVersion;
      this.variantRequest?.abort();
      this.availabilityRequest?.abort();
      this.variantRequest = new AbortController();
      const selected = this.optionValues();
      const quantity = Number(this.querySelector('[data-quantity]')?.value);
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', selected.join(','));
      if (!window.fetch) {
        // 极旧浏览器仍通过正常商品页导航选择规格，不能保留已经过时的隐藏 variant ID。
        this.setBusy('variant');
        window.location.assign(url);
        return;
      }
      url.searchParams.set('section_id', this.dataset.sectionId);
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      this.clearMessages();
      this.setBusy('variant');
      try {
        const response = await fetch(url, { signal: this.variantRequest.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Variant response failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (version !== this.requestVersion || !this.isConnected) return;
        const source = html.getElementById(this.id);
        const nextPurchase = source?.querySelector('[data-purchase]');
        if (!nextPurchase?.querySelector('[data-product-form]')) throw new Error('Variant response is incomplete');
        // DOMParser 的文档默认禁用脚本；其中 noscript 会被解析为真实样式节点。
        // 导入当前已启用 JS 的页面前移除备用内容，避免备用隐藏样式误伤规格控件。
        nextPurchase.querySelectorAll('noscript').forEach((fallback) => fallback.remove());
        this.querySelector('[data-purchase]').replaceWith(document.importNode(nextPurchase, true));
        this.dataset.selectedMedia = source.dataset.selectedMedia || '';
        this.committedOptions = this.optionValues();
        this.preserveQuantity(quantity);
        this.clearBusy();
        this.initializePayments();
        const nextMedia = this.findMedia(this.dataset.selectedMedia);
        if (nextMedia >= 0) this.selectMedia(nextMedia);
        const id = this.querySelector('[data-product-form] [name="id"]')?.value;
        const destination = new URL(location.href);
        if (id) {
          destination.searchParams.set('variant', id);
          destination.searchParams.delete('option_values');
        } else {
          destination.searchParams.delete('variant');
          destination.searchParams.set('option_values', this.committedOptions.join(','));
        }
        history.replaceState(history.state, '', destination);
        this.querySelectorAll('[data-option]')[focusIndex]?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.requestVersion || !this.isConnected) return;
        // 网络失败必须回滚可见选项，防止“看见新规格、却加入旧规格”。
        this.restoreOptions();
        this.clearBusy();
        this.showError('We couldn’t update this option. Your previous selection has been restored. Please try again.');
      }
    }

    cartComponent() {
      return [...document.querySelectorAll('cart-drawer, cart-notification')].find((component) =>
        typeof component.getSectionsToRender === 'function' && typeof component.renderContents === 'function');
    }

    validCartSections(component, response) {
      try {
        // Dawn notification 的 selector 在取得本次 cart item key 后才能正确生成。
        if (component.localName === 'cart-notification' && typeof response.key === 'string') component.cartItemKey = response.key;
        const sections = component.getSectionsToRender();
        return sections.length > 0 && sections.every((section) => {
          const text = response.sections?.[section.id];
          return typeof text === 'string' && new DOMParser().parseFromString(text, 'text/html').querySelector(section.selector || '.shopify-section');
        });
      } catch (_) { return false; }
    }

    async refreshAvailability(variantId) {
      if (!variantId || !window.fetch) return;
      this.availabilityRequest?.abort();
      const controller = new AbortController();
      this.availabilityRequest = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.delete('option_values');
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (controller.signal.aborted || !this.isConnected || this.querySelector('[data-product-form] [name="id"]')?.value !== String(variantId)) return;
        const source = html.getElementById(this.id)?.querySelector('[data-purchase]');
        if (!source || source.querySelector('[data-product-form] [name="id"]')?.value !== String(variantId)) return;
        const sourceButton = source.querySelector('[data-submit]');
        const button = this.querySelector('[data-submit]');
        const quantity = this.querySelector('[data-quantity]');
        const sourceQuantity = source.querySelector('[data-quantity]');
        if (!sourceButton || !button || !quantity || !sourceQuantity) return;
        // 只更新可售状态与数量，不替换购买面板，以保留购物车抽屉的焦点返回目标。
        const previousQuantity = Number(quantity.value);
        ['min', 'max', 'step'].forEach((attribute) => {
          if (sourceQuantity.hasAttribute(attribute)) quantity.setAttribute(attribute, sourceQuantity.getAttribute(attribute));
          else quantity.removeAttribute(attribute);
        });
        this.preserveQuantity(previousQuantity);
        const control = this.lockedControls.find((entry) => entry.element === button);
        if (control) control.disabled = sourceButton.disabled;
        else button.disabled = sourceButton.disabled;
        const label = sourceButton.querySelector('[data-submit-label]')?.textContent;
        if (this.busy === 'cart') this.submitText = label;
        else if (label !== undefined) button.querySelector('[data-submit-label]').textContent = label;
        const payment = this.querySelector('.shopify-payment-button');
        const sourcePayment = source.querySelector('.shopify-payment-button');
        if (payment && !sourcePayment) payment.remove();
        else if (!payment && sourcePayment) {
          const nextPayment = document.importNode(sourcePayment, true);
          this.querySelector('[data-product-form]').append(nextPayment);
          if (this.busy) { this.lockedPayments.push({ element: nextPayment, inert: false }); nextPayment.inert = true; }
          this.initializePayments();
        }
        this.syncQuantityButtons();
      } catch (_) {
        // 这是加购成功后的只读刷新，失败不表示加购失败，也不会触发再次加购。
      } finally { clearTimeout(timeout); }
    }

    async onSubmit(event) {
      const form = event.target;
      if (!form.matches('[data-product-form]') || !this.contains(form) || !window.fetch) return;
      event.preventDefault();
      if (this.busy || this.querySelector('[data-submit]')?.disabled || !form.reportValidity()) return;
      const variant = form.querySelector('[name="id"]');
      if (!variant?.value || variant.disabled) return this.showError('Please choose an available option.');
      this.availabilityRequest?.abort();
      const body = new FormData(form); // 必须在控件 disabled 之前读取，否则数量不会被提交。
      const component = this.cartComponent();
      if (component) {
        try {
          body.set('sections', [...new Set(component.getSectionsToRender().map((section) => section.id))].slice(0, 5).join(','));
          body.set('sections_url', location.pathname);
          component.setActiveElement?.(this.querySelector('[data-submit]'));
        } catch (_) { /* 自定义购物车组件不兼容时，下方确认成功后显示购物车链接。 */ }
      }
      this.clearMessages();
      this.setBusy('cart');
      let confirmed = false;
      try {
        const endpoint = new URL(this.dataset.cartAddUrl, location.origin);
        if (!endpoint.pathname.endsWith('.js')) endpoint.pathname += '.js';
        const response = await fetch(endpoint, {
          method: 'POST', credentials: 'same-origin', body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        const result = await response.json();
        if (!response.ok || Number(result.status) >= 400) {
          const message = typeof result.description === 'string' ? result.description : result.message;
          this.showError(typeof message === 'string' ? message : (this.dataset.errorLabel || 'This item couldn’t be added. Please try again.'));
          return;
        }
        confirmed = true;
        if (!this.isConnected) return;
        this.showSuccess('Added to your cart.');
        // Dawn 的订阅事件是附加通知，第三方订阅者出错不能把成功加购误判成失败。
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'taztir-purchase', productVariantId: variant.value, cartData: result
            })).catch(() => {});
          }
        } catch (_) { /* 继续完成已成功的购物车结果展示。 */ }
        if (!component) {
          window.location.assign(this.dataset.cartUrl);
        } else if (this.validCartSections(component, result)) {
          try {
            component.classList.remove('is-empty');
            component.renderContents(result);
          } catch (_) { this.showSuccess('Added to your cart. Open your cart to continue.'); }
        } else this.showSuccess('Added to your cart. Open your cart to continue.');
        if (component) await this.refreshAvailability(variant.value);
      } catch (_) {
        if (confirmed) this.showSuccess('Added to your cart. Open your cart to continue.');
        else this.showError('We couldn’t confirm the cart update. Please check your cart before trying again.');
      } finally {
        this.clearBusy();
      }
    }
  }

  customElements.define('taztir-purchase', TaztirPurchase);
})();
